// decorations.js
// Create visual decorations for reference citations

import {Decoration, DecorationSet} from "prosemirror-view";

/**
 * Create widget decoration for citation numbers at the end of a reference mark
 * @param {Array<number>} numbers - Sorted array of citation numbers
 * @returns {HTMLElement} Widget element
 */
export function createCitationWidget(numbers) {
    const span = document.createElement('sup');
    span.className = 'reference-citation-number';
    span.style.cssText = `
        color: #1976d2;
        font-weight: 600;
        font-size: 0.75em;
        margin-left: 1px;
        cursor: inherit;
        user-select: none;
    `;
    span.textContent = `[${numbers.join(',')}]`;
    return span;
}

/**
 * Create citation decorations for all reference marks in the document
 * Also adds data-reference-count attribute to paragraphs/headings containing reference marks
 * @param {Node} doc - ProseMirror document
 * @param {Schema} schema - ProseMirror schema
 * @param {Function} getCitationNumbers - Function (uid) => Array<number>
 * @param {Function} getReferenceCount - Function (uid) => number (count of references for this mark)
 * @returns {DecorationSet} Decoration set
 */
export function createCitationDecorations(doc, schema, getCitationNumbers, getReferenceCount) {
    const ranges = findReferenceMarkRanges(doc, schema);
    const decorations = [];

    // Widget decorations for citation numbers
    ranges.forEach(({uid, to}) => {
        const numbers = getCitationNumbers(uid);
        if (numbers && numbers.length > 0) {
            decorations.push(
                Decoration.widget(to, createCitationWidget(numbers), {
                    side: -1, // Place before position `to` so the widget stays inside the mark's span
                    key: `citation-${uid}`,
                })
            );
        }
    });

    // Node decorations for data-reference-count on paragraphs/headings
    const blockReferenceCounts = calculateBlockReferenceCounts(doc, schema, ranges, getReferenceCount);
    blockReferenceCounts.forEach(({pos, nodeSize, count}) => {
        decorations.push(
            Decoration.node(pos, pos + nodeSize, {
                'data-reference-count': String(count),
            })
        );
    });

    return DecorationSet.create(doc, decorations);
}

/**
 * Calculate reference counts for each block (paragraph/heading) containing reference marks
 * @param {Node} doc - ProseMirror document
 * @param {Schema} schema - ProseMirror schema
 * @param {Array<{uid: string, from: number, to: number}>} ranges - Reference mark ranges
 * @param {Function} getReferenceCount - Function (uid) => number
 * @returns {Array<{pos: number, nodeSize: number, count: number}>} Block reference counts
 */
function calculateBlockReferenceCounts(doc, schema, ranges, getReferenceCount) {
    if (!getReferenceCount) return [];

    const blockCounts = new Map(); // Map(pos -> {nodeSize, count})

    // For each reference mark range, find its parent block and accumulate counts
    ranges.forEach(({uid, from}) => {
        const $pos = doc.resolve(from);

        // Find the paragraph or heading ancestor
        for (let depth = $pos.depth; depth >= 0; depth--) {
            const node = $pos.node(depth);
            if (node.type.name === 'paragraph' || node.type.name === 'heading') {
                const blockPos = $pos.before(depth);
                const count = getReferenceCount(uid) || 0;

                if (blockCounts.has(blockPos)) {
                    const existing = blockCounts.get(blockPos);
                    existing.count += count;
                } else {
                    blockCounts.set(blockPos, {
                        nodeSize: node.nodeSize,
                        count: count,
                    });
                }
                break;
            }
        }
    });

    // Convert to array
    const result = [];
    blockCounts.forEach(({nodeSize, count}, pos) => {
        result.push({pos, nodeSize, count});
    });

    return result;
}

/**
 * Find all reference mark ranges in the document
 * Returns array of {uid, from, to} for each contiguous reference mark range
 * Properly handles overlapping/nested reference marks by tracking each UID separately
 * @param {Node} doc - ProseMirror document
 * @param {Schema} schema - ProseMirror schema
 * @returns {Array<{uid: string, from: number, to: number}>} Reference mark ranges
 */
export function findReferenceMarkRanges(doc, schema) {
    const referenceMark = schema.marks.reference;

    if (!referenceMark) return [];

    // Track open ranges for each UID to handle overlapping marks
    const openRanges = new Map(); // uid -> {uid, from, to}
    const completedRanges = [];

    doc.descendants((node, pos) => {
        if (!node.isText) {
            // Non-text node breaks all current ranges
            openRanges.forEach((range) => {
                completedRanges.push(range);
            });
            openRanges.clear();
            return;
        }

        // Get ALL reference marks on this text node (handles overlapping marks)
        const refMarks = node.marks.filter(m => m.type === referenceMark);
        const currentUids = new Set(refMarks.map(m => m.attrs.uid).filter(Boolean));

        // Close ranges for UIDs no longer present
        openRanges.forEach((range, uid) => {
            if (!currentUids.has(uid)) {
                completedRanges.push(range);
                openRanges.delete(uid);
            }
        });

        // Extend or start ranges for current UIDs
        currentUids.forEach((uid) => {
            if (openRanges.has(uid)) {
                // Extend existing range
                openRanges.get(uid).to = pos + node.nodeSize;
            } else {
                // Start new range
                openRanges.set(uid, {
                    uid,
                    from: pos,
                    to: pos + node.nodeSize,
                });
            }
        });
    });

    // Don't forget any remaining open ranges
    openRanges.forEach((range) => {
        completedRanges.push(range);
    });

    // Sort by starting position for consistent ordering
    completedRanges.sort((a, b) => a.from - b.from);

    return completedRanges;
}

